// Copyright 2017 Sorama B.V.

import { autoinject, BindingEngine, computedFrom, ICollectionObserverSplice } from "aurelia-framework";
import { GenericMeasurement, NewProjectDTO, ProjectDTO } from "datatypes/SoramaWebApi";
import { Status, StatusItem } from "datatypes/Status";
import { IPagedRequest } from "libraries/WebRequestHandler/IWebRequestHandler";
import { MeasurementRepository } from "repositories/MeasurementRepository";
import { ProjectRepository } from "repositories/ProjectRepository";
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import { IUserSettingsService } from "services/UserManagement/IUserSettingsService";

@autoinject
export class ProjectManagementService implements IProjectManagementService {
    //#region Constants
    public storageGroupKey = "ProjectManagementService";
    public currentProjectIdKey = "CurrentProjectId";
    //#endregion

    //#region Injected Services
    private readonly measurementRepository: MeasurementRepository;
    private readonly projectRepository: ProjectRepository;
    private readonly userSettingsService: IUserSettingsService;
    //#endregion

    //#region Private Properties

    private loadingMeasurements: boolean;
    private measurementsPromise: Promise<void>;
    private currentProjectPromise: Promise<ProjectDTO>;

    private get _currentProject(): ProjectDTO {
        return this.currentProject.item;
    }

    private _measurements: GenericMeasurement[] = [];
    //#endregion

    //#region IProjectManagementService Properties
    public currentProject: StatusItem<ProjectDTO>;

    @computedFrom("_measurements", "loadingProjects", "loadingMeasurements")
    public get measurements(): StatusItem<GenericMeasurement[]> {
        return {
            item: this._measurements,
            status: this.loadingMeasurements ? Status.createInProgress() : Status.createAvailable(),
        };
    }

    //#endregion

    constructor(
        measurementRepository: MeasurementRepository,
        projectRepository: ProjectRepository,
        userSettingsService: IUserSettingsService,
        bindingEngine: BindingEngine
    ) {
        this.measurementRepository = measurementRepository;
        this.projectRepository = projectRepository;
        this.userSettingsService = userSettingsService;

        bindingEngine.collectionObserver(measurementRepository.measurementIds).subscribe(this.onMeasurementIdsChanged);

        this.currentProject = {
            item: { id: "", name: "Loading...", created: new Date(), ownerId: "" },
            status: Status.createInProgress("Retrieving Current Project"),
        };

        // Get current project from user settings storage
        this.currentProjectPromise = this.userSettingsService
            .get(this.storageGroupKey, this.currentProjectIdKey)
            .then((currentProjectId) => {
                return this._getCurrentProject(currentProjectId);
            })
            .catch<ProjectDTO>(() => {
                return this.ensureProjectSelected();
            })
            .catch<ProjectDTO>((e) => {
                this.currentProject = {
                    item: undefined,
                    status: Status.createNotAvailable(e.message),
                };
                return undefined;
            })
            .finally(() => {
                this.currentProjectPromise = undefined;
            });
    }

    //#region IProjectManagementService Methods

    public getProjects(odataQueryFilter?: string): IPagedRequest<ProjectDTO> {
        return this.projectRepository.get(odataQueryFilter);
    }

    public setCurrentProject(projectId: string): Promise<void> {
        return this.setCurrentProjectWithPlaceholder({ id: projectId, name: "", created: new Date(), ownerId: "" });
    }

    public setCurrentProjectWithPlaceholder(project: ProjectDTO): Promise<void> {
        this.currentProject = new StatusItem<ProjectDTO>();
        this.currentProject = {
            item: project,
            status: Status.createInProgress("Loading Project"),
        };
        this.currentProjectPromise = this._getCurrentProject(project.id).then((x) => {
            this.currentProjectPromise = undefined;
            return x;
        });

        return this.currentProjectPromise.then((x) => {});
    }

    public getCurrentProject(): Promise<ProjectDTO> {
        if (this.currentProjectPromise) {
            return this.currentProjectPromise;
        }

        if (this.currentProject.status.available && this.currentProject.item) {
            Promise.resolve(this.currentProject.item);
        }
    }

    public createProject(projectName: string, projectDescription?: string): Promise<ProjectDTO> {
        return this.projectRepository.post(<NewProjectDTO>{
            name: projectName,
            description: projectDescription,
        });
    }

    public updateProject(projectId: string, newProperties: unknown): Promise<ProjectDTO> {
        return this.projectRepository.putProperties(projectId, newProperties);
    }

    public deleteProject(projectId: string): Promise<void> {
        return this.projectRepository.delete(projectId);
    }

    public createMeasurement(recordingGroupId: string): Promise<string> {
        if (!this._currentProject) return Promise.reject(new Error("No project is selected"));

        return this.measurementRepository.createGenericMeasurement(recordingGroupId, this._currentProject.id);
    }

    public refreshMeasurements(): Promise<void> {
        // The promise handles retrieving the project or ensuring one is selected;
        // Wait for the promise to complete if it exists before refreshing its measurements
        const projectPromise = this.currentProjectPromise
            ? this.currentProjectPromise
            : Promise.resolve(this._currentProject);
        return projectPromise.then(() => {
            const currentProjectItem = this.currentProject.item;
            if (currentProjectItem && currentProjectItem.id) {
                // Load all measurements from the current project
                return this.measurementRepository
                    .getMeasurementsForProject(this.currentProject.item.id)
                    .then((measurements) => {
                        this._measurements = measurements;
                    });
            }
            return Promise.reject("No project is selected");
        });
    }

    public tryRefreshMeasurements(): Promise<void> {
        this.refreshMeasurements();
        return Promise.resolve();
    }

    public getFavoriteProjectIds(): Promise<string[]> {
        return this.projectRepository.getFavoriteProjectIds();
    }

    public getProjectFavorited(projectId: string): Promise<boolean> {
        return this.projectRepository.getFavoriteProjectIds().then((x) => x.includes(projectId));
    }

    public setProjectFavorited(projectId: string, isFavorite?: boolean): Promise<void> {
        return this.projectRepository.setFavoriteProject(projectId, isFavorite);
    }

    private onMeasurementIdsChanged = (splices?: ICollectionObserverSplice<string>[]): void => {
        // If we dont have a current project we cannot update the projects
        if (!this._currentProject) {
            this._measurements = [];
            return;
        }

        // Update measurements
        this.loadingMeasurements = true;

        let singleSplice = false;
        let addedMeasurement: string = undefined;
        let removed: string[] = [];

        if (!!splices && splices.length === 1) {
            singleSplice = true;
            const splice = splices[0];
            if (splice.addedCount === 1) {
                addedMeasurement = this.measurementRepository.measurementIds[splice.index];
            }

            removed = splice.removed;
        }

        const promise = this.measurementsPromise ? this.measurementsPromise : Promise.resolve();

        this.measurementsPromise = promise
            .then(() => {
                if (singleSplice) {
                    const newMeasurements = this._measurements.filter((x) => removed.every((y) => x.id !== y));

                    if (!addedMeasurement || newMeasurements.some((x) => x.id === addedMeasurement)) {
                        this._measurements = newMeasurements;
                        return;
                    }
                    // Only 1 measurement added, just load that single measurement
                    return this.measurementRepository
                        .getMeasurement(addedMeasurement)
                        .then((measurement) => {
                            this._measurements = newMeasurements.concat(measurement);
                        })
                        .catch(() => {
                            this._measurements = newMeasurements;
                        });
                }

                // Complex change, load all measurements
                return this.measurementRepository
                    .getMeasurementsForProject(this._currentProject.id)
                    .then((measurements) => {
                        this._measurements = measurements;
                    });
            })
            .catch(() => {
                this._measurements = [];
            })
            .then(() => {
                this.loadingMeasurements = false;
            });
    };

    public async ensureProjectSelected(): Promise<ProjectDTO> {
        return this.getProjects()
            .readToEnd()
            .then((projects) => {
                if (projects.length == 0) {
                    return Promise.reject(new Error("The current user has no projects to select"));
                }

                const project = projects[0];
                this.currentProject = {
                    item: project,
                    status: Status.createAvailable(),
                };
                return project;
            })
            .catch((e) => {
                return Promise.reject(new Error("Failed to retrieve projects for the current user"));
            });
    }

    private _getCurrentProject(projectId: string): Promise<ProjectDTO> {
        return this.projectRepository
            .getById(projectId)
            .then((project) => {
                if (this.currentProject.item.id && project.id !== this.currentProject.item.id) {
                    // project selection is outdated, ignore and return the current project
                    return this.currentProject.item;
                }

                this.currentProject = {
                    item: project,
                    status: Status.createAvailable(),
                };

                // Save it to user settings
                this.userSettingsService.set(this.storageGroupKey, this.currentProjectIdKey, projectId);
                this.onMeasurementIdsChanged();

                return project;
            })
            .catch((e) => {
                return Promise.reject(new Error(`Project with id ${projectId} does not exist`));
            });
    }

    //#endregion
}
